// Shared KPI card layout math so the card render, the settings panel and the
// resize constraints all agree on how many metrics fit into the (fixed-height)
// section.

import type { CanvasCard, KPICardData, KpiCardConfig } from "store/types";
import { GRID_CONSTANTS } from "store/types/reports/report-generator";

export const KPI_MAX_METRICS = 5;

/**
 * Number of metrics actually rendered by a KPI card. Prefers the fetched data
 * (cubic1..N, what the user sees) and falls back to the configured cubics.
 */
export const countKpiMetrics = (card: CanvasCard): number => {
  const data = card.data as KPICardData | undefined;
  if (data) {
    let n = 0;
    for (let i = 1; i <= KPI_MAX_METRICS; i++) {
      if (data[`cubic${i}` as keyof KPICardData]) n++;
    }
    if (n > 0) return n;
  }
  if (card.config.type === "kpi") return Object.keys((card.config as KpiCardConfig).cubics ?? {}).length;
  return 0;
};

// The drag-handle bar shown on top of every card in edit mode eats into the
// content area, so a metric that fits in the exported report can still clip
// while editing. Account for it when judging "does it fit".
export const KPI_EDIT_CHROME_PX = 28;

export interface KpiLayout {
  cols: number;
  fontSize: number;
  padding: string;
  gap: string;
  labelSize: number;
  isCompact: boolean;
}

/** Responsive layout for `count` metrics at the given card width. */
export const getKpiLayout = (widthPercent: number, count: number): KpiLayout => {
  const isCompact = widthPercent <= 50;
  const cols = isCompact ? Math.min(count, 2) : Math.min(count, KPI_MAX_METRICS);
  const density = widthPercent / Math.max(cols, 1);
  const fontSize = density >= 50 ? 28 : density >= 33 ? 22 : density >= 20 ? 18 : 15;
  const padding = density >= 50 ? "20px 24px" : density >= 33 ? "16px 18px" : "12px 14px";
  const gap = density >= 33 ? "8px 20px" : "6px 12px";
  const labelSize = isCompact ? 9 : 10;
  return { cols, fontSize, padding, gap, labelSize, isCompact };
};

/**
 * Max number of metrics that fit into the card's section, based on its width and
 * fixed pixel height. Mirrors the grid layout in `KpiCard` (rows × columns).
 * `chromePx` is height consumed by surrounding UI (e.g. the edit-mode header).
 * Falls back to {@link KPI_MAX_METRICS} when the height is unknown.
 */
export const getKpiCapacity = (widthPercent: number, heightPx?: number, chromePx = 0): number => {
  if (!heightPx) return KPI_MAX_METRICS;

  const maxCols = widthPercent <= 50 ? 2 : KPI_MAX_METRICS;
  // Densest case: columns filled, so the per-cube height is at its smallest font.
  const { fontSize, padding, gap, labelSize } = getKpiLayout(widthPercent, maxCols);

  const paddingY = 2 * parseInt(padding, 10); // top + bottom (equal in our presets)
  const rowGap = parseInt(gap, 10); // CSS `gap: <row> <column>` → first value is the row gap

  // label (lineHeight ~1.5) + 4px margin + value (lineHeight 1.1)
  const cubeHeight = labelSize * 1.5 + 4 + fontSize * 1.1;

  const available = heightPx - paddingY - chromePx;
  // n rows occupy n*cubeHeight + (n-1)*rowGap. Not even one row may fit → 0.
  const rows = Math.max(0, Math.floor((available + rowGap) / (cubeHeight + rowGap)));

  return Math.min(rows * maxCols, KPI_MAX_METRICS);
};

/** Whether `count` metrics fit into a section of the given width/height. */
export const kpiMetricsFit = (count: number, widthPercent: number, heightPx?: number, chromePx = 0): boolean =>
  count <= getKpiCapacity(widthPercent, heightPx, chromePx);

/** Smallest grid height (rows) that fits `count` metrics at the given width. */
export const getKpiMinGridH = (widthPercent: number, count: number, chromePx = 0): number => {
  for (let h = 1; h <= GRID_CONSTANTS.GRID_COLUMNS; h++) {
    if (count <= getKpiCapacity(widthPercent, h * GRID_CONSTANTS.GRID_CELL_HEIGHT, chromePx)) return h;
  }
  return GRID_CONSTANTS.GRID_COLUMNS;
};
